import { ScreenSizes } from '../types/index.js';

/**
 * Checks if a value is a primitive (not an object or function).
 *
 * @template T - The type of the value being checked.
 * @param value - The value to check, which can be either a primitive or a responsive element.
 * @returns `true` if the value is a primitive; otherwise, `false`.
 *
 * ### Example
 * ```typescript
 * const isPrimitive = checkIsPrimitive(42); // true
 * const isNotPrimitive = checkIsPrimitive({ XS: 10 }); // false
 * ```
 */
function checkIsPrimitive(value) {
    return value === null || (typeof value !== 'object' && typeof value !== 'function');
}
/**
 * Checks if a value is an `IResponsiveElement` for a specific screen size.
 *
 * @template T - The type of the value being checked.
 * @param value - The value to check, which can be either a primitive or a responsive element.
 * @param screeSize - The screen size to check against.
 * @returns `true` if the value is an `IResponsiveElement` containing the specified screen size; otherwise, `false`.
 *
 * ### Example
 * ```typescript
 * const isResponsive = isResponsiveElement({ XS: 10 }, ScreenSizes.XS); // true
 * const isNotResponsive = isResponsiveElement(42, ScreenSizes.XS); // false
 * ```
 */
const isResponsiveElement = (value, screeSize) => typeof value === 'object' && value !== null && screeSize in value;
/**
 * Retrieves the default value for a responsive CSS property.
 *
 * @template T - The type of the CSS property value.
 * @param CSSProp - The CSS property, which can be a primitive or a responsive element.
 * @returns The default value for the property, or `undefined` if no default is found.
 *
 * ### Example
 * ```typescript
 * const defaultValue = getCSSPropDefaultValue({ XS: '10px', LG: '20px' });
 * console.log(defaultValue); // '20px' if ScreenSizes.LG is the first matching size
 * ```
 */
function getCSSPropDefaultValue(CSSProp) {
    if (!CSSProp)
        return undefined;
    const { XXL, XL, LG, MD, SM, XS, } = ScreenSizes;
    return ((isResponsiveElement(CSSProp, XXL) && CSSProp[XXL])
        || (isResponsiveElement(CSSProp, XL) && CSSProp[XL])
        || (isResponsiveElement(CSSProp, LG) && CSSProp[LG])
        || (isResponsiveElement(CSSProp, MD) && CSSProp[MD])
        || (isResponsiveElement(CSSProp, SM) && CSSProp[SM])
        || (isResponsiveElement(CSSProp, XS) && CSSProp[XS])
        || CSSProp);
}
/**
 * Computes the responsive object for a given CSS property or size.
 *
 * @template T - The type of the CSS property value.
 * @param size - The CSS property or size, which can be a primitive or a responsive element.
 * @returns A `ResponsiveObject<T>` containing the computed values for all screen sizes.
 *
 * ### Example
 * ```typescript
 * const responsiveness = computeResponsiveness({ XS: '10px', LG: '20px' });
 * console.log(responsiveness);
 * // {
 * //   Default: { value: '20px' },
 * //   XS: { value: '10px' },
 * //   LG: { value: '20px' },
 * //   ... // other screen sizes with undefined
 * // }
 * ```
 */
function computeResponsiveness(size) {
    const defaultValue = getCSSPropDefaultValue(size);
    const expectedProperties = checkIsPrimitive(size)
        ? undefined
        : Object.keys(defaultValue).reduce((acc, key) => {
            acc[key] = undefined;
            return acc;
        }, {});
    const { Default, XXL, XL, LG, MD, SM, XS, XXS, } = ScreenSizes;
    return {
        [Default]: { value: defaultValue },
        [XXL]: { value: isResponsiveElement(size, XXL) ? size[XXL] : expectedProperties },
        [XL]: { value: isResponsiveElement(size, XL) ? size[XL] : expectedProperties },
        [LG]: { value: isResponsiveElement(size, LG) ? size[LG] : expectedProperties },
        [MD]: { value: isResponsiveElement(size, MD) ? size[MD] : expectedProperties },
        [SM]: { value: isResponsiveElement(size, SM) ? size[SM] : expectedProperties },
        [XS]: { value: isResponsiveElement(size, XS) ? size[XS] : expectedProperties },
        [XXS]: { value: isResponsiveElement(size, XXS) ? size[XXS] : expectedProperties },
    };
}

export { computeResponsiveness, getCSSPropDefaultValue };
